import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { ControlContainer, FormControl, FormGroup } from "@angular/forms";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { LgdErrorStateMatcher, LgdFormControl, LgdFormControlRadioConfig } from "../lgdCommon";
@Component({
    selector: "lgd-radio-group",
    templateUrl: "./lgdRadioGroup.tmpl.html",
    styleUrls: ["./lgdRadioGroup.component.scss"],
    standalone: false
})
export class LgdRadioGroupComponent<T> implements OnInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();

    private _initialized: boolean;

    @Input()
    readonly controlName: string | number;

    @Input()
    readonly label: string;

    @Input()
    readonly hideLabel: boolean;

    @Input()
    readonly tooltip: string;

    @Input("options")
    readonly optionSource: Observable<T[]>;

    @Input() horizontal: boolean;

    readonly errorStateMatcher = new LgdErrorStateMatcher();

    constructor(private readonly controlContainer: ControlContainer) { }

    get initialized(): boolean
    {
        return this._initialized;
    }

    get formGroup(): FormGroup
    {
        return this.controlContainer.control as FormGroup;
    }

    get formControl(): FormControl<T>
    {
        if (!this.formGroup || (this.controlName !== undefined && this.controlName == null))
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        const controlNameStr = typeof this.controlName === "string" ? this.controlName : `${this.controlName}`;
        return this.formGroup.get(controlNameStr) as FormControl<T>;
    }

    get lgdFormControl(): LgdFormControl<LgdFormControlRadioConfig<T>>
    {
        return LgdFormControl.isLgdFormControl<LgdFormControlRadioConfig<T>>(this.formControl)
            ? this.formControl
            : undefined;
    }

    private _options: ReadonlyArray<Readonly<T>>;

    get options(): ReadonlyArray<T>
    {
        return this._options;
    }

    get layout(): "row" | "column" { return this.horizontal ? "row" : "column"; }

    ngOnInit(): void
    {
        if (!this.optionSource)
        {
            throw new Error(`options field is required. controlName was: ${JSON.stringify(this.controlName)}`);
        }
        console.debug(
            `${LgdRadioGroupComponent.name}.ngOnInit | ${this.controlName} | formControl`,
            this.formControl.value
        );
        this.optionSource.pipe(takeUntil(this.destructor$))
            .subscribe(_value =>
            {
                this._options = _value;
                // this.changeDetectorRef.markForCheck();
            });
        // this.formControl.valueChanges
        // 	.pipe(takeUntil(this.destructor$))
        // 	.subscribe(_value =>
        // 	{
        // 		this.changeDetectorRef.markForCheck();
        // 	});
        this._initialized = true;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    getDisplayValueFn(): (x: T) => string
    {
        if (!!this.lgdFormControl && !!this.lgdFormControl.data.displayValueFn)
        {
            return this.lgdFormControl.data.displayValueFn;
        }
        return (x: T) =>
        {
            return x ? x.toString() : "";
        };
    }

    getDisplayValue(x: T): string
    {
        return this.getDisplayValueFn()(x);
    }

    hasError(id: string): boolean
    {
        return !!this.formControl && this.formControl.hasError(id);
    }

    getOptionId(option: T): string
    {
        return `${this.controlName}_${this.getDisplayValue(option)}`;
    }

    get groupLabel(): string
    {
        return `Choose ${this.label} option`;
    }
}
